import React, { useEffect, useState } from "react";
import axios from "axios";
import "./LongtermTradesContainer.css";

function LongtermTradesContainer() {
  const [data, setData] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const token = localStorage.getItem("authToken");

  const axiosConfig = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        "  https://quatdashboard.com:8084/api/longtermtrades/all",
        axiosConfig,
      );
      setData(res.data || {});
    } catch (err) {
      console.error("❌ Error fetching longterm trades:", err);
      alert("Failed to load data!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleExpand = (clientId) => {
    setExpanded(expanded === clientId ? null : clientId);
  };

  const filteredClients = Object.keys(data).filter((clientId) =>
    clientId.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // 🧮 Calculate client summary
  const getClientSummary = (trades) => {
    const totalQty = trades.reduce((sum, t) => sum + t.qty, 0);
    const totalValue = trades.reduce((sum, t) => sum + t.entryprice * t.qty, 0);
    const avgPrice = totalQty > 0 ? totalValue / totalQty : 0;
    const totalPnL = trades.reduce((sum, t) => sum + t.pnl, 0);
    const totalCharges = trades.reduce((sum, t) => sum + t.charges, 0);

    const activeTrades = trades.filter((t) => t.status === "OPEN");
    const closedTrades = trades.filter((t) => t.status === "CLOSED");

    const activeGrouped = {};
    activeTrades.forEach((t) => {
      if (!activeGrouped[t.symbol]) {
        activeGrouped[t.symbol] = {
          symbol: t.symbol,
          qty: 0,
          totalPrice: 0,
          ltp: t.ltp,
        };
      }
      activeGrouped[t.symbol].qty += t.qty;
      activeGrouped[t.symbol].totalPrice += t.entryprice * t.qty;
      if (t.ltp) activeGrouped[t.symbol].ltp = t.ltp; // keep latest LTP
    });

    const closedGrouped = {};
    closedTrades.forEach((t) => {
      if (!closedGrouped[t.symbol]) {
        closedGrouped[t.symbol] = {
          symbol: t.symbol,
          qty: 0,
          totalPrice: 0,
          ltp: t.ltp,
        };
      }
      closedGrouped[t.symbol].qty += t.qty;
      closedGrouped[t.symbol].totalPrice += t.entryprice * t.qty;
      if (t.ltp) closedGrouped[t.symbol].ltp = t.ltp;
    });

    const activeSymbols = Object.values(activeGrouped).map((s) => ({
      symbol: s.symbol,
      qty: s.qty,
      avgPrice: s.totalPrice / s.qty,
      ltp: s.ltp,
    }));

    const closedSymbols = Object.values(closedGrouped).map((s) => ({
      symbol: s.symbol,
      qty: s.qty,
      avgPrice: s.totalPrice / s.qty,
      ltp: s.ltp,
    }));

    // 🧮 Total live PnL across all active symbols
    const totalLivePnL = activeSymbols.reduce((sum, s) => {
      return s.ltp ? sum + (s.ltp - s.avgPrice) * s.qty : sum;
    }, 0);

    return {
      avgPrice,
      totalPnL,
      totalCharges,
      totalQty,
      activeCount: activeTrades.length,
      closedCount: closedTrades.length,
      activeStockCount: activeSymbols.length,
      closedStockCount: closedSymbols.length,
      activeSymbols,
      closedSymbols,
      totalLivePnL,
    };
  };

  return (
    <div className="longterm-screen">
      <h2 className="page-title">📊 Longterm Trades</h2>

      <div className="toolbar">
        <input
          type="text"
          placeholder="🔍 Search client..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <button onClick={fetchData}>🔄 Refresh</button>
      </div>

      {loading ? (
        <div className="loader">Loading trade data...</div>
      ) : filteredClients.length === 0 ? (
        <div className="loader">No records found.</div>
      ) : (
        <div className="client-container">
          {filteredClients.map((clientId) => {
            const trades = data[clientId];
            const summary = getClientSummary(trades);

            return (
              <div key={clientId} className="client-card">
                {/* Summary Row */}
                <div
                  className="client-summary"
                  onClick={() => toggleExpand(clientId)}
                >
                  <div className="client-id">
                    <h3>{clientId}</h3>
                    {/* <p>
                      {summary.activeCount} Active Trades •{" "}
                      {summary.closedCount} Closed Trades • Total Qty:{" "}
                      {summary.totalQty}
                    </p> */}
                    <p className="stock-counts">
                      Active Stocks: {summary.activeStockCount} • Closed Stocks:{" "}
                      {summary.closedStockCount}
                    </p>

                    {/* 🧩 Stock summary side-by-side */}
                    <div className="symbol-summary-row">
                      {summary.activeSymbols.map((s) => (
                        <div key={s.symbol} className="symbol-chip active">
                          {s.symbol}: qty {s.qty} • avg ₹{s.avgPrice.toFixed(2)}{" "}
                          •
                          <span className="ltp">
                            {" "}
                            LTP ₹{s.ltp?.toFixed(2) || "—"}
                          </span>
                        </div>
                      ))}

                      {summary.closedSymbols.map((s) => (
                        <div key={s.symbol} className="symbol-chip closed">
                          {s.symbol}: qty {s.qty} • avg ₹{s.avgPrice.toFixed(2)}{" "}
                          •
                          <span className="ltp">
                            {" "}
                            LTP ₹{s.ltp?.toFixed(2) || "—"}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="summary-details">
                    <div>Charges: ₹{summary.totalCharges.toFixed(2)}</div>
                    <div
                      className={
                        summary.totalLivePnL >= 0 ? "positive" : "light-red"
                      }
                    >
                      Live PnL: ₹{summary.totalLivePnL.toFixed(2)}
                    </div>
                    <div
                      className={
                        summary.totalPnL >= 0 ? "positive" : "light-red"
                      }
                    >
                      Realized PnL: ₹{summary.totalPnL.toFixed(2)}
                    </div>
                    <button className="expand-btn">
                      {expanded === clientId ? "▲" : "▼"}
                    </button>
                  </div>
                </div>

                {/* Expanded Table */}
                {/* Expanded Table */}
                {expanded === clientId && (
                  <div className="trades-section">
                    <table className="trade-table">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Symbol</th>
                          <th>Qty</th>
                          <th>Entry Price</th>
                          <th>Exit Price</th>
                          <th>UPnL</th> {/* ✅ NEW COLUMN */}
                          <th>RPnL</th>
                          <th>Charges</th>
                          <th>Trade Date</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trades.map((t) => {
                          // ✅ Calculate live/unrealized PnL
                          const upnl =
                            t.ltp && t.status === "OPEN"
                              ? (t.ltp - t.entryprice) * t.qty
                              : 0;

                          return (
                            <tr key={t.id}>
                              <td>{t.id}</td>
                              <td className="symbol">{t.symbol}</td>
                              <td>{t.qty}</td>
                              <td>{t.entryprice.toFixed(2)}</td>
                              <td>{t.exitprice.toFixed(2)}</td>

                              {/* ✅ Display UPnL (unrealized) */}
                              <td
                                className={upnl >= 0 ? "positive" : "light-red"}
                              >
                                {upnl.toFixed(2)}
                              </td>

                              {/* Existing realized PnL */}
                              <td
                                className={
                                  t.pnl >= 0 ? "positive" : "light-red"
                                }
                              >
                                {t.pnl.toFixed(2)}
                              </td>

                              <td>{t.charges.toFixed(2)}</td>
                              <td>
                                {new Date(t.tradedate).toLocaleString("en-IN")}
                              </td>
                              <td
                                className={
                                  t.status === "OPEN" ? "positive" : "light-red"
                                }
                              >
                                {t.status}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default LongtermTradesContainer;
